import { create } from 'zustand'

/**
 * Centralized app hydration state management
 *
 * This store tracks whether critical app state has been restored from localStorage.
 * Components that depend on persisted state should check `isHydrated` before rendering
 * to prevent flash of incorrect UI (e.g., showing signup page when user is logged in).
 *
 * Hydration sources tracked:
 * - Auth session: tokens restored from localStorage
 * - KemCard store: screen state restored from localStorage (via zustand persist)
 *
 * Usage:
 * ```tsx
 * const isHydrated = useAppHydrationStore((s) => s.isHydrated)
 * if (!isHydrated) return null // or loading skeleton
 * ```
 */

interface AppHydrationState {
  // Individual hydration flags
  isAuthSessionRestored: boolean
  isKemCardStoreHydrated: boolean

  // Computed: true when all critical stores are hydrated
  isHydrated: boolean

  // Actions
  setAuthSessionRestored: () => void
  setKemCardStoreHydrated: () => void
}

export const useAppHydrationStore = create<AppHydrationState>((set, get) => ({
  isAuthSessionRestored: false,
  isKemCardStoreHydrated: false,
  isHydrated: false,

  setAuthSessionRestored: () => {
    set({ isAuthSessionRestored: true })
    // Recompute isHydrated
    const state = get()
    if (state.isKemCardStoreHydrated) {
      set({ isHydrated: true })
    }
  },

  setKemCardStoreHydrated: () => {
    set({ isKemCardStoreHydrated: true })
    // Recompute isHydrated
    const state = get()
    if (state.isAuthSessionRestored) {
      set({ isHydrated: true })
    }
  },
}))
